import React, { useEffect, useState, useRef } from 'react';
import './Message.css';
import userIcon from '../../Assets/Images/myuti-user.png';
import bubble from '../../Assets/Images/myuti-bubble.png';
import MessageReview from '../MessageReview/MessageReview';
import { useSelector } from 'react-redux';
import { getTenantConfig } from '../../store/Config/configSelectors';

function formatText(text) {
  if (typeof text !== 'string') {
    return text;
  }

  // Remove text enclosed in 【】
  let formattedText = text.replace(/【.*?】/g, '');

  // Replace #### with <h4> tags before handling newlines
  formattedText = formattedText.replace(/(^|\n)#### (.*)/g, '$1<h4>$2</h4>');

  // Replace ### with <h3> tags before handling newlines
  formattedText = formattedText.replace(/(^|\n)### (.*)/g, '$1<h3>$2</h3>');

  // Replace markdown bold and italic syntax with HTML tags
  formattedText = formattedText
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
    .replace(/\*(.*?)\*/g, '<em>$1</em>'); // Italic

  // Replace markdown-style links [text](url) with clickable links
  formattedText = formattedText.replace(
    /\[([^\]]+?)\]\((\S+?)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Replace newline characters with <br /> tags
  formattedText = formattedText.replace(/\n/g, '<br />');

  // Temporarily store existing links to avoid double replacement
  let linkPlaceholders = [];
  formattedText = formattedText.replace(/<a [^>]+>.*?<\/a>/g, match => {
    linkPlaceholders.push(match);
    return `__LINK_PLACEHOLDER_${linkPlaceholders.length - 1}__`;
  });

  // Regex to match email addresses
  const emailRegex = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
  // Replace email addresses with clickable mailto: links
  formattedText = formattedText.replace(emailRegex, email => {
    return `<a href="mailto:${email}" target="_blank" rel="noopener noreferrer">${email}</a>`;
  });

  // Regex to match URLs
  const urlRegex = /(\b(https?|ftp|file):\/\/\S+)/gi;
  // Replace URLs with clickable links
  formattedText = formattedText.replace(urlRegex, url => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });

  // Restore the original links
  formattedText = formattedText.replace(
    /__LINK_PLACEHOLDER_(\d+)__/g,
    (match, index) => {
      return linkPlaceholders[parseInt(index)];
    },
  );

  return formattedText;
}

const TYPING_SPEED = 10; // ms per character, adjust for smoothness

const Message = ({ message, user, messageFlagEnabled, onTextUpdate }) => {
  const [logoSrc, setLogoSrc] = useState('');
  const { logo } = useSelector(getTenantConfig);
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef(null);
  const prevFullTextRef = useRef('');

  useEffect(() => {
    const logoName = logo || 'ema-orb.svg';
    const images = require.context('../../Assets/Images', true);

    try {
      const logoImage = images(`./${logoName}`);
      setLogoSrc(logoImage);
    } catch (error) {
      console.error('Logo image could not be loaded:', error);
    }
  }, []); // Empty dependency array means this effect runs once after initial render

  // Animation effect for Ema's messages
  useEffect(() => {
    if (message.type !== 'ema') return;
    const fullText = message.message || '';
    let startIdx = displayedText.length;
    if (fullText === displayedText) {
      setIsAnimating(false);
      return;
    }
    setIsAnimating(true);
    if (animationRef.current) clearInterval(animationRef.current);
    animationRef.current = setInterval(() => {
      setDisplayedText(prev => {
        if (fullText.startsWith(prev) && prev.length < fullText.length) {
          const newText = fullText.slice(0, prev.length + 1);
          // Trigger scroll update every 3 characters to reduce frequency
          if (
            onTextUpdate &&
            (newText.length % 10 === 0 || newText.length === fullText.length)
          ) {
            onTextUpdate();
          }
          return newText;
        } else {
          clearInterval(animationRef.current);
          setIsAnimating(false);
          return fullText;
        }
      });
    }, TYPING_SPEED);
    return () => {
      if (animationRef.current) clearInterval(animationRef.current);
    };
    // eslint-disable-next-line
  }, [message.message, onTextUpdate]);

  // Reset displayedText if a new message comes in
  useEffect(() => {
    if (message.type === 'ema' && prevFullTextRef.current !== message.message) {
      if (displayedText.length > message.message.length) {
        setDisplayedText('');
      }
      prevFullTextRef.current = message.message;
    }
    // eslint-disable-next-line
  }, [message.message]);

  if (!message.message) return null;

  return (
    <div className="Chat-Container">
      {message.type === 'ema' ? (
        <div
          style={{
            justifyContent: 'flex-start',
            flexDirection: 'row',
            display: 'flex',
            marginTop: 20,
          }}
        >
          <img
            className="iconOrbLeft"
            style={{ height: 43, width: 43, marginRight: 10 }}
            src={bubble}
            alt="logoScr"
          />
          <div className="Message-Wrapper-Ema">
            <div>
              <div
                className="TextArea"
                dangerouslySetInnerHTML={{
                  __html: formatText(
                    isAnimating
                      ? displayedText + '<span class="blinking-cursor">|</span>'
                      : displayedText,
                  ),
                }}
              ></div>
              {/* {messageFlagEnabled && (
                <MessageReview message={message} user={user} />
              )} */}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginRight: 24,
          }}
        >
          <div className="Message-Wrapper-User">
            <div
              className="TextArea"
              dangerouslySetInnerHTML={{ __html: formatText(message.message) }}
            ></div>
          </div>
          <img
            src={userIcon}
            style={{ height: 43, width: 43, marginLeft: 8 }}
            alt="userIcon"
          />
        </div>
      )}
    </div>
  );
};

export default Message;
